import React from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import reportWebVitals from "./reportWebVitals";
import { defineCustomElements } from "@ionic/pwa-elements/loader";
import { AuthContextProvider } from "./context/AuthContext";
import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en.json";
import ru from "javascript-time-ago/locale/ru.json";
import { ProfileContextProvider } from "./context/ProfileContext";
import { BadgeContextProvider } from "./context/BadgeContext";
import { SchedJobContextProvider } from "./context/JobContextScheduler";
import { WerkerJobContextProvider } from "./context/JobContextWerker";
import { CrewContextProvider } from "./context/CrewContext";
import ErrorBoundary from "./components/ErrorBoundary";

// Initialize Sentry before anything else runs.
// Replace "YOUR_SENTRY_DSN" with your actual DSN from sentry.io.
Sentry.init({
  dsn: "https://de1c3c7a31f458c1a78ee93ba23b2a55@o4511101838295040.ingest.us.sentry.io/4511101839671296",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Capture 10% of transactions for performance monitoring
  tracesSampleRate: 0.1,
  // Capture 10% of sessions for session replay (records what user was doing before crash)
  replaysSessionSampleRate: 0.1,
  // Always capture session replay when an error occurs
  replaysOnErrorSampleRate: 1.0,
  // Set environment so you can filter prod vs dev in the dashboard
  environment: process.env.NODE_ENV,
});

TimeAgo.addDefaultLocale(en);
TimeAgo.addLocale(ru);

// Global error handlers — catch unhandled errors that escape React/Sentry's
// automatic instrumentation (e.g., errors in Capacitor plugin callbacks).
window.addEventListener("error", (event) => {
  console.error("[GlobalErrorHandler] Uncaught error:", {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error,
  });
  Sentry.captureException(event.error || new Error(event.message));
});

window.addEventListener("unhandledrejection", (event) => {
  console.error("[GlobalErrorHandler] Unhandled promise rejection:", {
    reason: event.reason,
  });
  Sentry.captureException(event.reason);
});

const container = document.getElementById("root");
const root = createRoot(container!);
root.render(
  <ErrorBoundary>
    <AuthContextProvider>
      <BadgeContextProvider>
        <ProfileContextProvider>
          <CrewContextProvider>
            <SchedJobContextProvider>
              <WerkerJobContextProvider>
                <React.StrictMode>
                  <App />
                </React.StrictMode>
              </WerkerJobContextProvider>
            </SchedJobContextProvider>
          </CrewContextProvider>
        </ProfileContextProvider>
      </BadgeContextProvider>
    </AuthContextProvider>
  </ErrorBoundary>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Call the element loader after the platform has been bootstrapped
defineCustomElements(window);
